import { Flex, Text } from "@chakra-ui/react";
import { DateRangeFormat } from "@intentsify/types";
import {
  formatDate,
  isPopulatedArray,
  toNumberDisplayValue,
} from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { createStore, Provider } from "jotai";
import { DateTime } from "luxon";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useMeasure } from "react-use";
import { byLook } from "theme/look";
import { TimeseriesItem, TimeseriesItemDataRecord } from "types";
import { ErrorBoundary } from "../../ErrorBoundary";
import { Loader } from "../../Loader";
import { NoData } from "../../NoData";
import {
  BrandChart,
  brandColourFor,
  ChartExportSvg,
  ToggleLegend,
  ToggleLegendItem,
  useBrandChartTheme,
} from "../BrandChart";
import { MIN_HEIGHT } from "../Charts.const";
import { TimeseriesTooltip } from "./components/TimeseriesTooltip/TimeseriesTooltip";
import { LegacyTimeseries } from "./legacy/Timeseries";
import {
  buildTimeseriesBarsDefinition,
  buildTimeseriesLinesDefinition,
  dataKeyDash,
  formatTimeLabel,
  getDataKeys,
  getTimeAxis,
  TimeseriesRow,
  toTimeseriesRows,
} from "./Timeseries.definition";
import { hoveredNearestPointAtomFamily } from "./Timeseries.state";
import { TimeseriesProps } from "./Timeseries.types";

export type { TimeseriesProps };

const LEGEND_RIGHT_MIN_WIDTH = 1200;

/** Calls the renderer during its own render, so its hooks run in scope. */
const RenderInScope = ({ render }: { render: () => ReactNode }) => (
  <>{render()}</>
);

/**
 * Renders a caller's tooltip for the focused point. Some renderers read the
 * hovered point from jotai state, so it is provided through a scoped store.
 */
const PointTooltip = ({
  item,
  isoDate,
  uniqueKey,
  render,
}: {
  item: TimeseriesItem<string, TimeseriesItemDataRecord<string>>;
  isoDate: string;
  uniqueKey: string;
  render: () => ReactNode;
}) => {
  const store = useMemo(() => createStore(), []);
  store.set(hoveredNearestPointAtomFamily(uniqueKey), {
    datum: item,
    coords: { x: 0, y: 0 },
    isoDate,
  });

  return (
    <Provider store={store}>
      <RenderInScope render={render} />
    </Provider>
  );
};

const BrandTimeseries = <K extends string, T extends string>({
  timeseries,
  data,
  dataMainKey,
  dataKeyLabels,
  id,
  title,
  exportMode,
  disableLegend = false,
  legendItemActions,
  disableDataSources = false,
  legendPosition,
  isLoading = false,
  dateFormat = DateRangeFormat.StartDateMonth,
  stretch = false,
  noDataMessage,
  pallete,
  tooltipRenderer = TimeseriesTooltip,
  useTwoYAxes = false,
  secondYAxisDataKeyLabel,
  scale = "linear",
  variant = "lines",
  minHeight = MIN_HEIGHT,
  startDate,
  defaultTimeGap,
}: TimeseriesProps<K, T>) => {
  const brandTheme = useBrandChartTheme();
  const [measureRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();
  const [uniqueKey] = useState(() => Math.random().toString());
  const [hiddenItems, setHiddenItems] = useState<number[]>([]);
  const [hiddenDataKeys, setHiddenDataKeys] = useState<string[]>([]);
  const [highlight, setHighlight] = useState<{
    itemIndex?: number;
    dataKey?: string;
  }>();

  // Data keys are read dynamically, so widen the per-point record.
  const anyData = data as TimeseriesItem<K, TimeseriesItemDataRecord<string>>[];
  const isBars = variant === "bars";
  const twoAxes = useTwoYAxes && data.length === 2;

  const colors = useMemo(
    () =>
      pallete?.length
        ? pallete.map((colour) => brandColourFor(colour, brandTheme.mode))
        : (brandTheme.theme.palette as string[]),
    [pallete, brandTheme],
  );

  const dataKeys = useMemo(() => {
    const keys = getDataKeys(anyData);

    // The bars variant plots the first series' first metric only.
    return isBars ? keys.slice(0, 1) : keys;
  }, [anyData, isBars]);

  const { times, gap } = useMemo(
    () => getTimeAxis(timeseries, { startDate, defaultTimeGap }),
    [timeseries, startDate, defaultTimeGap],
  );

  const formatTime = useCallback(
    (time: number) => formatTimeLabel(time, { timeseries, gap, dateFormat }),
    [timeseries, gap, dateFormat],
  );

  const labelFor = useCallback(
    (dataKey: string) =>
      (dataKeyLabels as Record<string, string> | undefined)?.[dataKey] ??
      dataKey,
    [dataKeyLabels],
  );

  const rows = useMemo(
    () =>
      toTimeseriesRows(isBars ? anyData.slice(0, 1) : anyData, {
        dataMainKey,
        dataKeys,
        hiddenItems,
        hiddenDataKeys,
        useTwoYAxes: twoAxes,
        scale,
      }),
    [
      anyData,
      isBars,
      dataMainKey,
      dataKeys,
      hiddenItems,
      hiddenDataKeys,
      twoAxes,
      scale,
    ],
  );

  const leftAxisLabel = dataKeys
    .filter((key) => !hiddenDataKeys.includes(key))
    .map(labelFor)
    .join(" / ");

  const buildDefinition = useCallback(() => {
    const options = {
      rows,
      times,
      formatTime,
      brandTheme,
      colors,
      dataKeys,
      scale,
      leftAxisLabel,
      rightAxisLabel: twoAxes
        ? `${secondYAxisDataKeyLabel ?? ""} volume`.trim()
        : undefined,
      highlight,
    };

    return isBars
      ? buildTimeseriesBarsDefinition(options)
      : buildTimeseriesLinesDefinition(options);
  }, [
    rows,
    times,
    formatTime,
    brandTheme,
    colors,
    dataKeys,
    scale,
    leftAxisLabel,
    twoAxes,
    secondYAxisDataKeyLabel,
    highlight,
    isBars,
  ]);

  const definition = useMemo(
    () => defineChart({ chart: buildDefinition, tooltip: { use: tooltip } }),
    [buildDefinition],
  );

  const legendItems = useMemo(
    (): ToggleLegendItem[] =>
      anyData.map((item, index) => ({
        key: String(index),
        label: String(item[dataMainKey]),
        color: colors[index % colors.length],
        swatch: true,
        hidden: hiddenItems.includes(index),
        actions: legendItemActions?.(
          item as TimeseriesItem<K, TimeseriesItemDataRecord<T>>,
        ),
      })),
    [anyData, dataMainKey, colors, hiddenItems, legendItemActions],
  );

  const dataSourceItems = useMemo(
    (): ToggleLegendItem[] =>
      dataKeys.map((dataKey, index) => ({
        key: dataKey,
        label: labelFor(dataKey),
        color: brandTheme.theme.foreground ?? "currentColor",
        strokeWidth: 2.5,
        strokeDasharray: dataKeyDash(index),
        hidden: hiddenDataKeys.includes(dataKey),
      })),
    [dataKeys, labelFor, brandTheme, hiddenDataKeys],
  );

  const toggle = <V,>(values: V[], value: V) =>
    values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(data) || !isPopulatedArray(times)) {
    return (
      <NoData height={minHeight} message={noDataMessage} stretch={stretch} />
    );
  }

  const ariaLabel = title ?? leftAxisLabel ?? "Timeseries chart";

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel}
        brandTheme={brandTheme}
        buildDefinition={buildDefinition}
        legend={legendItems
          .filter((item) => !item.hidden)
          .map(({ label, color }) => ({ label, color, swatch: true }))}
      />
    );
  }

  const legendRight =
    (legendPosition ??
      (containerWidth > LEGEND_RIGHT_MIN_WIDTH ? "right" : "bottom")) ===
    "right";
  const direction = legendRight ? "column" : "row";

  return (
    <ErrorBoundary>
      <Flex
        ref={measureRef}
        direction={legendRight ? "row" : "column"}
        gap={legendRight ? 5 : 3}
        width="100%"
        height="100%"
      >
        <BrandChart
          definition={definition}
          brandTheme={brandTheme}
          ariaLabel={ariaLabel}
          minHeight={minHeight}
          height={stretch ? undefined : minHeight}
          flexGrow={stretch ? 1 : undefined}
          renderTooltipBody={({ points }) => {
            const row = points[0]?.datum as TimeseriesRow | undefined;

            if (!row) {
              return null;
            }

            if (isBars) {
              return (
                <Text fontSize="sm">
                  <strong>
                    {formatDate({
                      date: DateTime.fromISO(row.isoDate).toJSDate(),
                    })}
                    :
                  </strong>{" "}
                  {toNumberDisplayValue(row.value ?? 0)}
                </Text>
              );
            }

            return (
              <PointTooltip
                item={
                  anyData[row.itemIndex] as TimeseriesItem<
                    string,
                    TimeseriesItemDataRecord<string>
                  >
                }
                isoDate={row.isoDate}
                uniqueKey={uniqueKey}
                render={() =>
                  tooltipRenderer({
                    tooltipData: data[row.itemIndex],
                    dataMainKey,
                    dataKeyLabels,
                    timeseries,
                    data,
                    textColor: brandTheme.theme.foreground ?? "",
                    uniqueKey,
                  })
                }
              />
            );
          }}
        />

        {!disableLegend && (
          <Flex
            direction="column"
            gap={2}
            flexShrink={0}
            maxWidth={legendRight ? "320px" : undefined}
          >
            {!disableDataSources && dataSourceItems.length > 1 && (
              <ToggleLegend
                items={dataSourceItems}
                direction={direction}
                onToggle={(key) =>
                  setHiddenDataKeys((keys) => toggle(keys, key))
                }
                onHighlight={(key) =>
                  setHighlight(key ? { dataKey: key } : undefined)
                }
              />
            )}
            <ToggleLegend
              items={legendItems}
              direction={direction}
              onToggle={(key) =>
                setHiddenItems((items) => toggle(items, Number(key)))
              }
              onHighlight={(key) =>
                setHighlight(key ? { itemIndex: Number(key) } : undefined)
              }
            />
          </Flex>
        )}
      </Flex>
    </ErrorBoundary>
  );
};

const Timeseries = byLook(BrandTimeseries, LegacyTimeseries);

export { Timeseries };
